import { CodeOutlined, DeleteOutlined, EditOutlined, LeftOutlined, PlusOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { AutoComplete, Button, Checkbox, Col, Flex, Form, Input, InputNumber, Radio, Row, Select, Space, Tabs, TimePicker, Tooltip } from "antd";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { getAddressSuggestionsV2, getFacilityLookups, getFacilityPlaceDetails, getGoogleMapsApiKey } from "../../service/httpsCalls";
import "./facility.scss";

declare global {
    interface Window {
        google?: any;
        facilityGoogleMapsPromise?: Promise<void>;
    }
}

export interface FacilityDetail {
    id: number;
    facilityName: string;
    facilityServiceType?: string[];
    isActive?: boolean;
    isFacilityOnGoogle?: boolean;
    addressLine1: string;
    address?: string;
    addressLine2?: string;
    city: string;
    stateCode?: string;
    zipcode: string;
    county?: string;
    countryCode: string;
    mailingAddressLine1?: string;
    mailingAddressLine2?: string;
    mailingCity?: string;
    mailingStateCode?: string;
    mailingZipcode?: string;
    mailingCounty?: string;
    mailingCountryCode?: string;
    isSameAsPhysicalAddress?: boolean;
    latitude?: number;
    longitude?: number;
    placeId?: string;
    mainPhone?: string;
    alternatePhone?: string;
    fax?: string;
    email?: string;
    timeZoneId?: string;
    businessHoursJson?: string;
    addressDataJson?: string;
    geocodeResponseJson?: string;
    placeDetailsResponseJson?: string;
    destinationAPiResponseJson?: string;
    treatmentTypes?: string[];
    notes?: string;
    tags?: string[];
    pickupPoints?: FacilityPickupPoint[];
    clientTreatmentTypes?: FacilityClientTreatmentType[];
}

export interface FacilityPickupPoint {
    id: number;
    pickupPointCode?: string;
    pickupPointName: string;
    pointType: string;
    sectionName?: string;
    floorLevel?: string;
    landmark?: string;
    instructions?: string;
    pickupAddress?: string;
    latitude: number;
    longitude: number;
    placeId?: string;
    isDefault: boolean;
    notes?: string;
}

export interface FacilityClientTreatmentType {
    id?: number;
    clientId: number;
    treatmentTypes: string[];
    actions?: string[];
}

interface CreateFacilityProps {
    facility?: FacilityDetail | null;
    onCancel: () => void;
    onSaved: (facility: FacilityDetail) => void;
    notify: (type: "success" | "error" | "warning" | "info", message: string) => void;
    saving?: boolean;
}

interface FacilityFormValues extends FacilityDetail {
    pickupPointCode?: string;
    pickupPointName?: string;
    pickupPointType?: string;
    pickupSectionName?: string;
    pickupFloorLevel?: string;
    pickupLandmark?: string;
    pickupInstructions?: string;
    pickupAddress?: string;
    pickupLatitude?: number;
    pickupLongitude?: number;
    pickupPlaceId?: string;
    pickupIsDefault?: boolean;
    pickupNotes?: string;
}

interface BusinessHourPeriod {
    open?: dayjs.Dayjs;
    close?: dayjs.Dayjs;
}

interface BusinessHourDay {
    day: string;
    closed: boolean;
    periods: BusinessHourPeriod[];
}

interface FacilitySelectOption {
    value: string;
    label: string;
}

interface FacilityLookupItem {
    id?: number;
    key?: string;
    Key?: string;
    value?: string | number;
    Value?: string | number;
    name?: string;
    Name?: string;
}

interface ClientLookupItem {
    id?: number;
    Id?: number;
    value?: string | number;
    Value?: string | number;
    name?: string;
    Name?: string;
    clientId?: number;
    ClientId?: number;
    clientName?: string;
    ClientName?: string;
    companyName?: string;
    CompanyName?: string;
}

interface ClientTreatmentTypeRow {
    id: number;
    clientId?: string;
    treatmentTypes: string[];
    actions: string[];
}

interface FacilityPlaceOption {
    value: string;
    label: ReactNode;
    placeId?: string;
}

interface FacilityPlaceDetails {
    placeId?: string;
    facilityName?: string;
    formattedAddress?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    stateCode?: string;
    county?: string;
    zipcode?: string;
    countryCode?: string;
    latitude?: number;
    longitude?: number;
    mainPhone?: string;
    timeZoneId?: string;
    geocodeResponseJson?: string;
    placeDetailsResponseJson?: string;
    destinationAPiResponseJson?: string;
    weekdayDescriptions?: string[];
}

interface PickupPointMapPreviewProps {
    apiKey: string;
    facilityLatitude?: number;
    facilityLongitude?: number;
    point: FacilityPickupPoint;
    fallbackMapUrl: string;
}

const fallbackStateOptions = ["AZ", "CA", "FL", "NC", "NM", "OH", "SC", "TN", "TX", "WI"]
    .map(value => ({ value, label: value }));

const countyOptions = ["Bernalillo", "Buncombe", "Davidson", "Hamilton", "Hillsborough", "Horry", "Knox", "Maricopa", "Mecklenburg", "Milwaukee"]
    .map(value => ({ value, label: value }));

const fallbackTreatmentTypeOptions = ["Dialysis", "Oncology", "Physical Therapy", "Primary Care", "Inpatient", "Outpatient", "Behavioral Health"]
    .map(value => ({ value, label: value }));

const clientTreatmentActionOptions: FacilitySelectOption[] = [
    { value: "Allow", label: "Allow" },
    { value: "Block", label: "Block" },
    { value: "AllowPickupOnly", label: "Allow Pickup Only" },
    { value: "AllowDropOffOnly", label: "Allow Drop Off Only" },
    { value: "VerifyAppointment", label: "Verify Appointment" }
];

const createDefaultClientTreatmentRow = (): ClientTreatmentTypeRow => ({
    id: Date.now(),
    treatmentTypes: [],
    actions: ["Allow"]
});

const normalizeClientTreatmentRows = (rows?: FacilityClientTreatmentType[]): ClientTreatmentTypeRow[] => {
    const normalizedRows = (rows ?? [])
        .filter(row => row.clientId)
        .map(row => ({
            id: row.id ?? row.clientId,
            clientId: row.clientId.toString(),
            treatmentTypes: row.treatmentTypes ?? [],
            actions: row.actions?.length ? row.actions : ["Allow"]
        }));

    return normalizedRows.length ? normalizedRows : [{ id: 1, treatmentTypes: [], actions: ["Allow"] }];
};

const fallbackFacilityServiceTypeOptions = [
    { value: "Dialysis Centers", label: "Dialysis Centers" },
    { value: "Mental Health Services", label: "Mental Health Services" },
    { value: "Pharmacy", label: "Pharmacy" }
];

const pointTypeOptions = ["Pickup", "Drop", "Both"]
    .map(value => ({ value, label: value }));

const zipCodeRules = [
    { required: true, whitespace: true, message: "Zipcode is required." },
    { pattern: /^\d{5}(-\d{4})?$/, message: "Zipcode must be 5 digits or in 12345-6789 format." },
    { max: 10 }
];

const usPhonePattern = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
const usPhoneMessage = "Phone number must be a valid US number.";

const optionalUsPhoneRules = [
    { pattern: usPhonePattern, message: usPhoneMessage },
    { max: 20 }
];

const formatPhoneNumber = (value?: string) => {
    const digits = (value ?? "").replace(/\D/g, "").slice(0, 10);

    if (digits.length <= 3) {
        return digits ? `(${digits}` : "";
    }

    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const fallbackTimeZoneOptions: FacilitySelectOption[] = [
    { value: "America/New_York", label: "America/New_York" },
    { value: "America/Chicago", label: "America/Chicago" },
    { value: "America/Denver", label: "America/Denver" },
    { value: "America/Los_Angeles", label: "America/Los_Angeles" }
];

const fullDayOpenTime = "12:00 AM";
const fullDayCloseTime = "11:59 PM";
const pickupMapDefaultZoom = 19;

const formatTimeValue = (value?: dayjs.Dayjs) => value?.format("hh:mm A") ?? "";

const parseTimeValue = (value?: string | null) => {
    const match = value?.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return undefined;

    let hours = Number(match[1]);
    const minutes = Number(match[2]);
    const meridiem = match[3].toUpperCase();

    if (meridiem === "PM" && hours !== 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;

    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
};

const createFullDayPeriod = (): BusinessHourPeriod => ({
    open: parseTimeValue(fullDayOpenTime),
    close: parseTimeValue(fullDayCloseTime)
});

const initialBusinessHours: BusinessHourDay[] = [
    { day: "Monday", closed: false, periods: [] },
    { day: "Tuesday", closed: false, periods: [] },
    { day: "Wednesday", closed: false, periods: [] },
    { day: "Thursday", closed: false, periods: [] },
    { day: "Friday", closed: false, periods: [] },
    { day: "Saturday", closed: false, periods: [] },
    { day: "Sunday", closed: false, periods: [] }
];

const serializeBusinessHours = (days: BusinessHourDay[]) => JSON.stringify(days.map(day => ({
    day: day.day,
    closed: day.closed,
    periods: day.closed
        ? []
        : day.periods
            .filter(period => period.open && period.close)
            .map(period => ({
                open: formatTimeValue(period.open),
                close: formatTimeValue(period.close)
            }))
})));

const parseSavedBusinessHours = (businessHoursJson?: string): BusinessHourDay[] => {
    if (!businessHoursJson) return initialBusinessHours;

    try {
        const parsed = JSON.parse(businessHoursJson) as Array<{ day: string; closed: boolean; periods?: Array<{ open?: string | null; close?: string | null }> }>;

        return initialBusinessHours.map(defaultDay => {
            const savedDay = parsed.find(item => item.day === defaultDay.day);
            if (!savedDay) return defaultDay;

            return {
                day: defaultDay.day,
                closed: savedDay.closed,
                periods: savedDay.closed ? [] : (savedDay.periods ?? []).map(period => ({
                    open: parseTimeValue(period.open),
                    close: parseTimeValue(period.close)
                }))
            };
        });
    } catch {
        return initialBusinessHours;
    }
};

const parseGoogleWeekdayDescriptions = (weekdayDescriptions?: string[]): BusinessHourDay[] | null => {
    if (!weekdayDescriptions?.length) return null;

    const normalize = (value: string) => value
        .replace(/\u2013|\u2014/g, "-")
        .replace(/[^\x20-\x7E]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const parsePeriod = (value: string): BusinessHourPeriod => {
        const [open, close] = normalize(value).split(/\s+-\s+/);
        return {
            open: parseTimeValue(open),
            close: parseTimeValue(close)
        };
    };

    return initialBusinessHours.map(defaultDay => {
        const description = weekdayDescriptions.find(item => normalize(item).startsWith(`${defaultDay.day}:`));
        if (!description) return defaultDay;

        const hoursText = normalize(description).replace(`${defaultDay.day}:`, "").trim();
        if (!hoursText || hoursText.toLowerCase() === "closed") {
            return { day: defaultDay.day, closed: true, periods: [] };
        }

        if (hoursText.toLowerCase() === "open 24 hours") {
            return {
                day: defaultDay.day,
                closed: false,
                periods: [createFullDayPeriod()]
            };
        }

        return {
            day: defaultDay.day,
            closed: false,
            periods: hoursText.split(",").map(parsePeriod)
        };
    });
};

const loadGoogleMapsScript = (apiKey: string) => {
    if (window.google?.maps) {
        return Promise.resolve();
    }

    if (window.facilityGoogleMapsPromise) {
        return window.facilityGoogleMapsPromise;
    }

    window.facilityGoogleMapsPromise = new Promise<void>((resolve, reject) => {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Unable to load Google Maps."));
        document.head.appendChild(script);
    });

    return window.facilityGoogleMapsPromise;
};

const normalizeGoogleMapsApiKey = (response: any) => {
    if (typeof response === "string") {
        return response.trim();
    }

    return (response?.apiKey ?? response?.ApiKey ?? response?.googleAddressAPI ?? response?.GoogleAddressAPI ?? "").toString().trim();
};

interface FacilityJsonPreview {
    label: string;
    value?: string | number | boolean | null;
}

interface LocationPointPreview {
    pointName: string;
    usage: string;
    travelMode: string;
}

interface ParsedFacilityJson {
    parsed?: any;
    formatted: string;
    isValid: boolean;
    isEmpty: boolean;
}

const parseFacilityJson = (value?: string): ParsedFacilityJson => {
    if (!value?.trim()) {
        return {
            formatted: "",
            isValid: true,
            isEmpty: true
        };
    }

    try {
        const parsed = JSON.parse(value);
        return {
            parsed,
            formatted: JSON.stringify(parsed, null, 2),
            isValid: true,
            isEmpty: false
        };
    } catch {
        return {
            formatted: value,
            isValid: false,
            isEmpty: false
        };
    }
};

const parseNestedJson = (value: any): any => {
    if (typeof value !== "string") {
        return value;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return value;
    }

    try {
        return JSON.parse(trimmedValue);
    } catch {
        return value;
    }
};

const getJsonValue = (source: any, paths: string[][]) => {
    for (const path of paths) {
        const value = path.reduce<any>((current, key) => current?.[key], source);
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }

    return undefined;
};

const buildGeocodePreview = (parsed: any): FacilityJsonPreview[] => {
    const firstResult = parsed?.results?.[0] ?? parsed?.Results?.[0];
    const location = firstResult?.geometry?.location ?? firstResult?.Geometry?.Location;

    return [
        { label: "Status", value: getJsonValue(parsed, [["status"], ["Status"]]) },
        { label: "Matched Address", value: getJsonValue(firstResult, [["formatted_address"], ["formattedAddress"], ["FormattedAddress"]]) },
        { label: "Place ID", value: getJsonValue(firstResult, [["place_id"], ["placeId"], ["PlaceId"]]) },
        { label: "Latitude", value: getJsonValue(location, [["lat"], ["latitude"], ["Latitude"]]) },
        { label: "Longitude", value: getJsonValue(location, [["lng"], ["longitude"], ["Longitude"]]) }
    ].filter(item => item.value !== undefined);
};

const buildPlaceDetailsPreview = (parsed: any): FacilityJsonPreview[] => [
    { label: "Place ID", value: getJsonValue(parsed, [["id"], ["placeId"], ["PlaceId"]]) },
    { label: "Name", value: getJsonValue(parsed, [["displayName", "text"], ["facilityName"], ["FacilityName"]]) },
    { label: "Formatted Address", value: getJsonValue(parsed, [["formattedAddress"], ["FormattedAddress"]]) },
    { label: "Phone", value: getJsonValue(parsed, [["nationalPhoneNumber"], ["internationalPhoneNumber"], ["mainPhone"], ["MainPhone"]]) },
    { label: "Time Zone", value: getJsonValue(parsed, [["timeZone", "id"], ["timeZoneId"], ["TimeZoneId"]]) },
    { label: "Open Now", value: getJsonValue(parsed, [["regularOpeningHours", "openNow"], ["openNow"], ["OpenNow"]]) }
].filter(item => item.value !== undefined);

const buildAddressDataPreview = (parsed: any): FacilityJsonPreview[] => [
    { label: "Facility Name", value: getJsonValue(parsed, [["facilityName"], ["FacilityName"]]) },
    { label: "Formatted Address", value: getJsonValue(parsed, [["formattedAddress"], ["FormattedAddress"]]) },
    { label: "Address Line 1", value: getJsonValue(parsed, [["addressLine1"], ["AddressLine1"]]) },
    { label: "City", value: getJsonValue(parsed, [["city"], ["City"]]) },
    { label: "State", value: getJsonValue(parsed, [["stateCode"], ["StateCode"]]) },
    { label: "Zipcode", value: getJsonValue(parsed, [["zipcode"], ["Zipcode"]]) },
    { label: "Place ID", value: getJsonValue(parsed, [["placeId"], ["PlaceId"]]) }
].filter(item => item.value !== undefined);

const formatPlaceType = (value: string) => value
    .replace(/_/g, " ")
    .replace(/\b\w/g, letter => letter.toUpperCase());

const getPlaceDetailTypes = (parsed: any): string[] => {
    const types = getJsonValue(parsed, [["types"], ["Types"]]);

    return Array.isArray(types) ? types.filter(item => typeof item === "string") : [];
};

const getGoogleMapUri = (parsed: any): string | undefined => {
    const uri = getJsonValue(parsed, [["googleMapsUri"], ["googleMapUri"], ["GoogleMapsUri"], ["GoogleMapUri"]]);

    return typeof uri === "string" && uri.trim() ? uri.trim() : undefined;
};

const formatBusinessHourPeriod = (period: BusinessHourPeriod) => {
    const open = formatTimeValue(period.open);
    const close = formatTimeValue(period.close);

    if (open.toLowerCase() === fullDayOpenTime.toLowerCase() && close.toLowerCase() === fullDayCloseTime.toLowerCase()) {
        return "Open 24 hours";
    }

    if (!open && !close) {
        return "Hours not specified";
    }

    return `${open || "Open"} - ${close || "Close"}`;
};

const formatBusinessHourDay = (day: BusinessHourDay) => {
    if (day.closed) {
        return "Closed";
    }

    if (!day.periods.length) {
        return "Hours not specified";
    }

    return day.periods.map(formatBusinessHourPeriod).join(", ");
};

const getGoogleBusinessHourDescriptions = (parsed: any): string[] => {
    const descriptions = getJsonValue(parsed, [
        ["regularOpeningHours", "weekdayDescriptions"],
        ["regularOpeningHours", "weekdayText"],
        ["currentOpeningHours", "weekdayDescriptions"],
        ["opening_hours", "weekday_text"],
        ["RegularOpeningHours", "WeekdayDescriptions"]
    ]);

    return Array.isArray(descriptions) ? descriptions.filter(item => typeof item === "string") : [];
};

const getPlaceDetailsJsonWithDestination = (placeDetailsJson?: string, destinationJson?: string) => {
    const placeDetailsParsed = parseFacilityJson(placeDetailsJson);
    const destinationParsed = parseFacilityJson(destinationJson);
    const destinationPoints = getNavigationPoints(destinationParsed.parsed);

    if (!placeDetailsParsed.parsed || !destinationPoints.length || getNavigationPoints(placeDetailsParsed.parsed).length) {
        return placeDetailsJson;
    }

    return JSON.stringify({
        ...placeDetailsParsed.parsed,
        navigationPoints: destinationPoints
    });
};

const normalizeStringArray = (value: any): string[] => {
    if (Array.isArray(value)) {
        return value.map(item => item?.toString().trim()).filter(Boolean);
    }

    if (typeof value === "string" && value.trim()) {
        return value.split(",").map(item => item.trim()).filter(Boolean);
    }

    return [];
};

const isNavigationPointLike = (value: any) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }

    return Boolean(getJsonValue(value, [
        ["navigationPointToken"],
        ["navigation_point_token"],
        ["NavigationPointToken"],
        ["displayName"],
        ["display_name"],
        ["DisplayName"],
        ["pointName"],
        ["point_name"],
        ["PointName"],
        ["location"],
        ["Location"],
        ["travelModes"],
        ["travel_modes"],
        ["TravelModes"],
        ["usage"],
        ["Usage"]
    ]));
};

const getNavigationPoints = (parsed: any): any[] => {
    const normalizedParsed = parseNestedJson(parsed);
    const navigationPoints = getJsonValue(normalizedParsed, [
        ["navigationPoints"],
        ["navigation_points"],
        ["NavigationPoints"],
        ["locationPoints"],
        ["location_points"],
        ["LocationPoints"]
    ]);

    if (Array.isArray(navigationPoints)) return navigationPoints;
    if (Array.isArray(normalizedParsed)) {
        return normalizedParsed.filter(isNavigationPointLike);
    }

    const visited = new Set<any>();
    const queue = [normalizedParsed];

    while (queue.length) {
        const current = parseNestedJson(queue.shift());

        if (!current || typeof current !== "object" || visited.has(current)) {
            continue;
        }

        visited.add(current);

        if (Array.isArray(current)) {
            if (current.some(isNavigationPointLike)) {
                return current.filter(isNavigationPointLike);
            }

            current.forEach(item => queue.push(item));
            continue;
        }

        Object.values(current).forEach(value => {
            const parsedValue = parseNestedJson(value);
            if (Array.isArray(parsedValue) && parsedValue.some(isNavigationPointLike)) {
                queue.unshift(parsedValue);
            } else if (parsedValue && typeof parsedValue === "object") {
                queue.push(parsedValue);
            }
        });
    }

    return [];
};

const getNavigationPointDisplayName = (point: any): string => {
    const displayName = getJsonValue(point, [
        ["displayName", "text"],
        ["displayName", "Text"],
        ["displayName"],
        ["display_name", "text"],
        ["display_name"],
        ["DisplayName", "Text"],
        ["DisplayName", "text"],
        ["DisplayName"],
        ["name"],
        ["Name"],
        ["pointName"],
        ["point_name"],
        ["PointName"]
    ]);

    return typeof displayName === "string" ? displayName.trim() : "";
};

const getNavigationPointUsage = (point: any): string[] => normalizeStringArray(getJsonValue(point, [
    ["usage"],
    ["usages"],
    ["usageType"],
    ["usageTypes"],
    ["usage_type"],
    ["usage_types"],
    ["Usage"],
    ["Usages"],
    ["UsageType"],
    ["UsageTypes"],
    ["pointType"],
    ["point_type"],
    ["PointType"]
]));

const getNavigationPointTravelModes = (point: any): string[] => normalizeStringArray(getJsonValue(point, [
    ["travelModes"],
    ["travel_modes"],
    ["TravelModes"],
    ["travelMode"],
    ["travel_mode"],
    ["TravelMode"]
]));

const hasDriveTravelMode = (point: any) =>
    getNavigationPointTravelModes(point).some(mode => mode.toLowerCase() === "drive");

const getNavigationPointLocation = (point: any) => {
    const location = getJsonValue(point, [["location"], ["Location"], ["geo"], ["Geo"]]) ?? point;
    const latitude = getJsonValue(location, [["latitude"], ["Latitude"], ["lat"], ["Lat"]]);
    const longitude = getJsonValue(location, [["longitude"], ["Longitude"], ["lng"], ["Lng"]]);

    return {
        latitude: Number(latitude),
        longitude: Number(longitude)
    };
};

const getNavigationPointName = (point: any, fallbackSequence: number, needsSequence: boolean) => {
    const displayName = getNavigationPointDisplayName(point);
    if (displayName) return displayName;

    const usage = getNavigationPointUsage(point);
    if (usage.some(item => item.toLowerCase().includes("parking"))) {
        return "Parking";
    }

    return needsSequence ? `Main Point ${fallbackSequence}` : "Main Point";
};

const getNavigationPointType = (usage: string[]) => {
    const hasPickup = usage.some(item => item.toLowerCase().includes("pickup") || item.toLowerCase().includes("pick up"));
    const hasDrop = usage.some(item => item.toLowerCase().includes("drop"));

    if (hasPickup && !hasDrop) return "PickUp";
    if (hasDrop && !hasPickup) return "DropOff";
    return "Both";
};

const getNavigationPointNoteUsage = (usage: string[]) => usage.filter(item => {
    const normalized = item.toLowerCase();
    return !normalized.includes("pickup") && !normalized.includes("pick up") && !normalized.includes("drop");
});

const buildNavigationPickupPoints = (placeDetailsJson?: string): FacilityPickupPoint[] => {
    const parsedJson = parseFacilityJson(placeDetailsJson);
    const drivePoints = getNavigationPoints(parsedJson.parsed)
        .filter(hasDriveTravelMode)
        .filter(point => {
            const location = getNavigationPointLocation(point);
            return Number.isFinite(location.latitude) && Number.isFinite(location.longitude);
        });
    const unnamedPointCount = drivePoints.filter(point => !getNavigationPointDisplayName(point)).length;
    let unnamedSequence = 0;

    return drivePoints.map((point, index) => {
        const usage = getNavigationPointUsage(point);
        const travelModes = getNavigationPointTravelModes(point);
        const location = getNavigationPointLocation(point);
        const hasName = Boolean(getNavigationPointDisplayName(point));
        const noteUsage = getNavigationPointNoteUsage(usage);
        const noteParts = [
            noteUsage.length ? `Usage: ${noteUsage.join(", ")}` : undefined,
            travelModes.length ? `Travel Mode: ${travelModes.join(", ")}` : undefined
        ].filter(Boolean);

        if (!hasName) {
            unnamedSequence += 1;
        }

        return {
            id: Date.now() + index,
            pickupPointCode: "",
            pickupPointName: getNavigationPointName(point, unnamedSequence, unnamedPointCount > 1).slice(0, 150),
            pointType: getNavigationPointType(usage),
            latitude: location.latitude,
            longitude: location.longitude,
            isDefault: index === 0,
            notes: noteParts.join("\n")
        };
    });
};

const buildLocationPointRows = (parsed: any): LocationPointPreview[] => {
    const points = getNavigationPoints(parsed);
    const filteredPoints = points;
    const unnamedPointCount = filteredPoints.filter(point => !getNavigationPointDisplayName(point)).length;
    let unnamedSequence = 0;

    return filteredPoints.map(point => {
        const usage = getNavigationPointUsage(point);
        const travelModes = getNavigationPointTravelModes(point);
        if (!getNavigationPointDisplayName(point)) {
            unnamedSequence += 1;
        }

        return {
            pointName: getNavigationPointName(point, unnamedSequence, unnamedPointCount > 1),
            usage: usage.length ? usage.join(", ") : "Both",
            travelMode: travelModes.length ? travelModes.join(", ") : "Not specified"
        };
    });
};

function BusinessHoursPreview({ googleDescriptions, savedBusinessHoursJson }: { googleDescriptions?: string[]; savedBusinessHoursJson?: string }) {
    let rows = googleDescriptions?.length
        ? googleDescriptions.map(description => {
            const [day, ...hoursParts] = description.split(":");
            return {
                day: day?.trim() || "Day",
                hours: hoursParts.join(":").trim() || "Hours not specified"
            };
        })
        : parseSavedBusinessHours(savedBusinessHoursJson).map(day => ({
            day: day.day,
            hours: formatBusinessHourDay(day)
        }));

    if (rows.length && rows.every(row => row.hours === "Hours not specified")) {
        rows = [{ day: "Business Hours", hours: "Not specified" }];
    }

    return (
        <div className="facility-json-hours">
            <div className="facility-json-section-title">Business Hours</div>
            <div className="facility-json-hours-list">
                {rows.map(row => (
                    <div className="facility-json-hours-row" key={row.day}>
                        <span>{row.day}</span>
                        <strong>{row.hours}</strong>
                    </div>
                ))}
            </div>
        </div>
    );
}

function LocationPointsTable({ rows }: { rows: LocationPointPreview[] }) {
    if (!rows.length) {
        return null;
    }

    return (
        <div className="facility-json-location-points">
            <div className="facility-json-section-title">Location Points</div>
            <div className="facility-json-location-table">
                <div className="facility-json-location-header">Point Name</div>
                <div className="facility-json-location-header">Usage</div>
                <div className="facility-json-location-header">Travel Mode</div>
                {rows.map((point, index) => (
                    <div className="facility-json-location-row" key={`${point.pointName}-${index}`}>
                        <div>{point.pointName}</div>
                        <div>{point.usage}</div>
                        <div>{point.travelMode}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PlaceDetailsExtras({ parsed, savedBusinessHoursJson, destinationJson }: { parsed: any; savedBusinessHoursJson?: string; destinationJson?: string }) {
    const mapUri = getGoogleMapUri(parsed);
    const types = getPlaceDetailTypes(parsed);
    const destinationParsed = parseFacilityJson(destinationJson);
    const parsedLocationPointRows = buildLocationPointRows(parsed);
    const locationPointRows = parsedLocationPointRows.length
        ? parsedLocationPointRows
        : buildLocationPointRows(destinationParsed.parsed);

    return (
        <>
            {(mapUri || types.length > 0) && (
                <div className="facility-json-place-extras">
                    {types.length > 0 && (
                        <div className="facility-json-place-type-row">
                            <span>Place Types</span>
                            <strong>{types.map(formatPlaceType).join(", ")}</strong>
                        </div>
                    )}
                    {mapUri && (
                        <Button
                            className="facility-json-map-link"
                            size="small"
                            type="link"
                            href={mapUri}
                            target="_blank"
                            rel="noreferrer"
                            icon={<ZoomInOutlined />}
                        >
                            View on Map
                        </Button>
                    )}
                </div>
            )}
            <BusinessHoursPreview
                googleDescriptions={getGoogleBusinessHourDescriptions(parsed)}
                savedBusinessHoursJson={savedBusinessHoursJson}
            />
            <LocationPointsTable rows={locationPointRows} />
        </>
    );
}

function FacilityJsonResponsePanel({
    title,
    subtitle,
    json,
    previewBuilder,
    children
}: {
    title: string;
    subtitle: string;
    json?: string;
    previewBuilder: (parsed: any) => FacilityJsonPreview[];
    children?: (parsed: any) => ReactNode;
}) {
    const parsedJson = parseFacilityJson(json);
    const previewItems = parsedJson.parsed ? previewBuilder(parsedJson.parsed) : [];
    const [showRawJson, setShowRawJson] = useState(false);

    return (
        <div className="facility-json-panel">
            <div className="facility-json-panel-header">
                <div>
                    <div className="facility-json-panel-title">{title}</div>
                    <div className="facility-json-panel-subtitle">{subtitle}</div>
                </div>
                <span className={`facility-json-status ${parsedJson.isEmpty ? "empty" : parsedJson.isValid ? "ready" : "invalid"}`}>
                    {parsedJson.isEmpty ? "No data" : parsedJson.isValid ? "JSON saved" : "Raw text"}
                </span>
            </div>

            {previewItems.length ? (
                <div className="facility-json-preview-grid">
                    {previewItems.map(item => (
                        <div className="facility-json-preview-item" key={item.label}>
                            <span>{item.label}</span>
                            <strong>{String(item.value)}</strong>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="facility-json-empty">
                    {parsedJson.isEmpty ? "This response has not been stored for this facility yet." : "Preview is unavailable for this JSON shape."}
                </div>
            )}

            {parsedJson.parsed && children?.(parsedJson.parsed)}

            <div className="facility-json-actions">
                <Button
                    size="small"
                    icon={<CodeOutlined />}
                    onClick={() => setShowRawJson(value => !value)}
                    disabled={parsedJson.isEmpty}
                >
                    {showRawJson ? "Hide JSON" : "Show JSON"}
                </Button>
            </div>

            {showRawJson && (
                <>
                    <div className="facility-json-raw-title">Raw JSON</div>
                    <pre className="facility-json-raw">{parsedJson.formatted || "No JSON available."}</pre>
                </>
            )}
        </div>
    );
}

function PickupPointMapPreview({ apiKey, facilityLatitude, facilityLongitude, point, fallbackMapUrl }: PickupPointMapPreviewProps) {
    const mapCanvasRef = useRef<HTMLDivElement>(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!apiKey) {
            setReady(false);
            return;
        }

        const mapElement = mapCanvasRef.current;
        const facilityLat = Number(facilityLatitude);
        const facilityLng = Number(facilityLongitude);
        const pickupLat = Number(point.latitude);
        const pickupLng = Number(point.longitude);

        if (!mapElement
            || !Number.isFinite(facilityLat)
            || !Number.isFinite(facilityLng)
            || !Number.isFinite(pickupLat)
            || !Number.isFinite(pickupLng)) {
            setReady(false);
            return;
        }

        let isMounted = true;
        let facilityMarker: any;
        let pickupMarker: any;
        let distanceLine: any;

        loadGoogleMapsScript(apiKey)
            .then(() => {
                if (!isMounted || !window.google?.maps) return;

                const google = window.google;
                const facilityPosition = { lat: facilityLat, lng: facilityLng };
                const pickupPosition = { lat: pickupLat, lng: pickupLng };
                const map = new google.maps.Map(mapElement, {
                    center: facilityPosition,
                    zoom: pickupMapDefaultZoom,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false
                });

                facilityMarker = new google.maps.Marker({
                    map,
                    position: facilityPosition,
                    title: "Facility",
                    optimized: false,
                    icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                });
                pickupMarker = new google.maps.Marker({
                    map,
                    position: pickupPosition,
                    title: point.pickupPointName || "Pickup Point",
                    optimized: false,
                    icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                });
                distanceLine = new google.maps.Polyline({
                    map,
                    path: [facilityPosition, pickupPosition],
                    strokeColor: "#1677ff",
                    strokeOpacity: 0.8,
                    strokeWeight: 2
                });

                const bounds = new google.maps.LatLngBounds();
                bounds.extend(facilityPosition);
                bounds.extend(pickupPosition);
                map.fitBounds(bounds);
                google.maps.event.addListenerOnce(map, "bounds_changed", () => {
                    if (map.getZoom() > pickupMapDefaultZoom) {
                        map.setZoom(pickupMapDefaultZoom);
                    }
                });
                google.maps.event.addListenerOnce(map, "idle", () => {
                    if (isMounted) {
                        setReady(true);
                    }
                });
            })
            .catch(() => {
                if (isMounted) {
                    setReady(false);
                }
            });

        return () => {
            isMounted = false;
            facilityMarker?.setMap(null);
            pickupMarker?.setMap(null);
            distanceLine?.setMap(null);
        };
    }, [apiKey, facilityLatitude, facilityLongitude, point.latitude, point.longitude, point.pickupPointName]);

    return (
        <div className="facility-pickup-card-map">
            <iframe
                title={`Pickup point map ${point.id}`}
                src={fallbackMapUrl}
                loading="lazy"
            />
            {apiKey && (
                <div
                    ref={mapCanvasRef}
                    className={`facility-pickup-card-google-map${ready ? " ready" : ""}`}
                />
            )}
        </div>
    );
}

export default function CreateFacility({ facility, onCancel, onSaved, notify, saving = false }: CreateFacilityProps) {
    const [form] = Form.useForm<FacilityFormValues>();
    const facilityNameSearchRef = useRef<number>();
    const physicalAddressSearchRef = useRef<number>();
    const facilityMapCanvasRef = useRef<HTMLDivElement>(null);
    const facilityGoogleMapElementRef = useRef<HTMLDivElement | null>(null);
    const facilityGoogleMapRef = useRef<any>(null);
    const facilityMarkerRef = useRef<any>(null);
    const facilityMarkerClickListenerRef = useRef<any>(null);
    const facilityPickupMarkersRef = useRef<any[]>([]);
    const facilityPickupMarkerListenersRef = useRef<any[]>([]);
    const facilityInfoWindowRef = useRef<any>(null);
    const pickupMapCanvasRef = useRef<HTMLDivElement>(null);
    const pickupGoogleMapElementRef = useRef<HTMLDivElement | null>(null);
    const pickupGoogleMapRef = useRef<any>(null);
    const pickupGoogleMapListenerRef = useRef<any>(null);
    const pickupFacilityMarkerRef = useRef<any>(null);
    const pickupSelectedMarkerRef = useRef<any>(null);
    const pickupDistanceLineRef = useRef<any>(null);
    const [pickupPoints, setPickupPoints] = useState<FacilityPickupPoint[]>(facility?.pickupPoints ?? []);
    const [showPickupForm, setShowPickupForm] = useState(false);
    const [editingPickupPointId, setEditingPickupPointId] = useState<number | null>(null);
    const [businessHours, setBusinessHours] = useState<BusinessHourDay[]>(() => parseSavedBusinessHours(facility?.businessHoursJson));
    const [geocodeResponseJson, setGeocodeResponseJson] = useState(facility?.geocodeResponseJson ?? "");
    const [placeDetailsResponseJson, setPlaceDetailsResponseJson] = useState(facility?.placeDetailsResponseJson ?? "");
    const [destinationAPiResponseJson, setDestinationAPiResponseJson] = useState(facility?.destinationAPiResponseJson ?? "");
    const [stateOptions, setStateOptions] = useState<FacilitySelectOption[]>(fallbackStateOptions);
    const [treatmentTypeOptions, setTreatmentTypeOptions] = useState<FacilitySelectOption[]>(fallbackTreatmentTypeOptions);
    const [facilityTagOptions, setFacilityTagOptions] = useState<FacilitySelectOption[]>([]);
    const [clientOptions, setClientOptions] = useState<FacilitySelectOption[]>([]);
    const [clientTreatmentRows, setClientTreatmentRows] = useState<ClientTreatmentTypeRow[]>(() => normalizeClientTreatmentRows(facility?.clientTreatmentTypes));
    const [facilityServiceTypeOptions, setFacilityServiceTypeOptions] = useState<FacilitySelectOption[]>(fallbackFacilityServiceTypeOptions);
    const [timeZoneOptions, setTimeZoneOptions] = useState<FacilitySelectOption[]>(fallbackTimeZoneOptions);
    const [facilityPlaceOptions, setFacilityPlaceOptions] = useState<FacilityPlaceOption[]>([]);
    const [facilityPlaceLoading, setFacilityPlaceLoading] = useState(false);
    const [physicalAddressOptions, setPhysicalAddressOptions] = useState<FacilityPlaceOption[]>([]);
    const [physicalAddressLoading, setPhysicalAddressLoading] = useState(false);
    const [isPhysicalAddressLocked, setIsPhysicalAddressLocked] = useState(Boolean(facility?.placeId));
    const [facilityMapQuery, setFacilityMapQuery] = useState(facility?.facilityName ?? "");
    const [facilityMapZoom, setFacilityMapZoom] = useState(15);
    const pickupMapZoom = Math.max(facilityMapZoom, pickupMapDefaultZoom);
    const [pickupMapRefreshKey, setPickupMapRefreshKey] = useState(0);
    const [googleMapsApiKey, setGoogleMapsApiKey] = useState("");
    const [googleMapError, setGoogleMapError] = useState("");
    const [facilityGoogleMapReady, setFacilityGoogleMapReady] = useState(false);
    const [pickupGoogleMapReady, setPickupGoogleMapReady] = useState(false);
    const [activeTab, setActiveTab] = useState("facility-address");
    const [lookupLoading, setLookupLoading] = useState(false);
    const [isSameAsPhysicalAddress, setIsSameAsPhysicalAddress] = useState(Boolean(facility?.isSameAsPhysicalAddress));
    const isEdit = Boolean(facility);
    const tabKeys = ["facility-address", "pickup-points", "contact-hours", "treatment-notes"];
    const currentTabIndex = tabKeys.indexOf(activeTab);

    const initialValues: Partial<FacilityFormValues> = useMemo(() => ({
        countryCode: "US",
        mailingCountryCode: "US",
        isActive: true,
        isSameAsPhysicalAddress: false,
        ...facility
    }), [facility]);

    useEffect(() => {
        let isMounted = true;

        getGoogleMapsApiKey()
            .then(response => {
                if (!isMounted) return;

                const apiKey = normalizeGoogleMapsApiKey(response);

                if (apiKey) {
                    setGoogleMapsApiKey(apiKey);
                    setGoogleMapError("");
                } else {
                    setGoogleMapsApiKey("");
                    setGoogleMapError("");
                }
            })
            .catch(() => {
                if (isMounted) {
                    setGoogleMapsApiKey("");
                    setGoogleMapError("");
                }
            });

        const mapStateOptions = (items?: FacilityLookupItem[]) => (items ?? [])
            .map(item => {
                const value = item.value?.toString() ?? item.Value?.toString() ?? "";

                return {
                    value,
                    label: value
                };
            })
            .filter(option => option.value);

        const mapLookupOptions = (items?: FacilityLookupItem[]) => {
            const optionMap = new Map<string, FacilitySelectOption>();

            (items ?? []).forEach(item => {
                const value = item.value?.toString() ?? item.Value?.toString() ?? item.id?.toString() ?? "";
                const label = item.name?.trim() ?? item.Name?.trim() ?? item.value?.toString().trim() ?? item.Value?.toString().trim() ?? "";
                const key = label.toLowerCase();

                if (value && label && !optionMap.has(key)) {
                    optionMap.set(key, { value, label });
                }
            });

            return Array.from(optionMap.values());
        };

        const mapFacilityServiceTypeOptions = (items?: FacilityLookupItem[]) => {
            const optionMap = new Map<string, FacilitySelectOption>();

            (items ?? []).forEach(item => {
                const value = item.name?.trim() ?? item.Name?.trim() ?? item.value?.toString() ?? item.Value?.toString() ?? "";
                const label = item.name?.trim() ?? item.Name?.trim() ?? "";
                const optionKey = value.toLowerCase();

                if (value && label && !optionMap.has(optionKey)) {
                    optionMap.set(optionKey, { value, label });
                }
            });

            return Array.from(optionMap.values());
        };

        const mapClientOptions = (items?: ClientLookupItem[]) => (items ?? [])
            .map(item => {
                const value = item.value?.toString() ?? item.Value?.toString() ?? (item.clientId ?? item.ClientId)?.toString() ?? item.id?.toString() ?? item.Id?.toString() ?? "";
                const label = item.name?.trim() ?? item.Name?.trim() ?? item.companyName?.trim() ?? item.CompanyName?.trim() ?? item.clientName?.trim() ?? item.ClientName?.trim() ?? "";

                return { value, label };
            })
            .filter(option => option.value && option.label);

        setLookupLoading(true);
        getFacilityLookups()
            .then(response => {
                if (!isMounted || !response) return;

                const loadedStates = mapStateOptions(response.stateList ?? response.StateList);
                const loadedTreatmentTypes = mapLookupOptions(response.treatmentTypeList ?? response.TreatmentTypeList);
                const loadedFacilityTags = mapLookupOptions(response.facilityTagList ?? response.FacilityTagList);
                const loadedFacilityServiceTypes = mapFacilityServiceTypeOptions(response.facilityServiceTypeList ?? response.FacilityServiceTypeList);
                const loadedTimeZones = mapLookupOptions(response.timeZoneList ?? response.TimeZoneList);
                const loadedClients = mapClientOptions(response.clientList ?? response.ClientList);

                if (loadedStates.length) {
                    setStateOptions(loadedStates);
                }

                if (loadedTreatmentTypes.length) {
                    setTreatmentTypeOptions(loadedTreatmentTypes);
                }

                setFacilityTagOptions(loadedFacilityTags);

                if (loadedFacilityServiceTypes.length) {
                    setFacilityServiceTypeOptions(loadedFacilityServiceTypes);
                }

                if (loadedTimeZones.length) {
                    setTimeZoneOptions(loadedTimeZones);
                }

                setClientOptions(loadedClients);
            })
            .catch(() => {
                if (isMounted) {
                    notify("warning", "Unable to load facility dropdown values.");
                }
            })
            .finally(() => {
                if (isMounted) {
                    setLookupLoading(false);
                }
            });

        return () => {
            isMounted = false;
            window.clearTimeout(facilityNameSearchRef.current);
            window.clearTimeout(physicalAddressSearchRef.current);
            if (facilityMarkerClickListenerRef.current) {
                facilityMarkerClickListenerRef.current.remove();
                facilityMarkerClickListenerRef.current = null;
            }
            facilityPickupMarkerListenersRef.current.forEach(listener => listener.remove());
            facilityPickupMarkerListenersRef.current = [];
            facilityPickupMarkersRef.current.forEach(marker => marker.setMap(null));
            facilityPickupMarkersRef.current = [];
            if (pickupGoogleMapListenerRef.current) {
                pickupGoogleMapListenerRef.current.remove();
                pickupGoogleMapListenerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (activeTab !== "facility-address") {
            setFacilityGoogleMapReady(false);
            return;
        }

        if (!googleMapsApiKey) return;

        const mapElement = facilityMapCanvasRef.current;
        const facilityCoordinates = getValidCoordinates(form.getFieldValue("latitude"), form.getFieldValue("longitude"));

        if (!mapElement || !facilityCoordinates) return;

        let isMounted = true;

        loadGoogleMapsScript(googleMapsApiKey)
            .then(() => {
                if (!isMounted || !window.google?.maps) return;
                setGoogleMapError("");

                const google = window.google;
                const facilityPosition = {
                    lat: facilityCoordinates.latitude,
                    lng: facilityCoordinates.longitude
                };
                const markerTitle = getFacilityAddressText();
                const shouldCreateMap = !facilityGoogleMapRef.current || facilityGoogleMapElementRef.current !== mapElement;

                if (shouldCreateMap) {
                    if (facilityMarkerClickListenerRef.current) {
                        facilityMarkerClickListenerRef.current.remove();
                        facilityMarkerClickListenerRef.current = null;
                    }
                    facilityMarkerRef.current = null;
                    facilityPickupMarkerListenersRef.current.forEach(listener => listener.remove());
                    facilityPickupMarkerListenersRef.current = [];
                    facilityPickupMarkersRef.current.forEach(marker => marker.setMap(null));
                    facilityPickupMarkersRef.current = [];
                    facilityInfoWindowRef.current = null;
                    facilityGoogleMapElementRef.current = mapElement;
                    setFacilityGoogleMapReady(false);

                    facilityGoogleMapRef.current = new google.maps.Map(mapElement, {
                        center: facilityPosition,
                        zoom: facilityMapZoom,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: true
                    });
                } else {
                    facilityGoogleMapRef.current.setCenter(facilityPosition);
                    facilityGoogleMapRef.current.setZoom(facilityMapZoom);
                }

                const map = facilityGoogleMapRef.current;
                google.maps.event.trigger(map, "resize");
                map.setCenter(facilityPosition);

                if (!facilityInfoWindowRef.current) {
                    facilityInfoWindowRef.current = new google.maps.InfoWindow();
                }

                if (!facilityMarkerRef.current) {
                    facilityMarkerRef.current = new google.maps.Marker({
                        map,
                        title: markerTitle,
                        optimized: false,
                        icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                    });
                }

                facilityMarkerRef.current.setPosition(facilityPosition);
                facilityMarkerRef.current.setTitle(markerTitle);

                if (facilityMarkerClickListenerRef.current) {
                    facilityMarkerClickListenerRef.current.remove();
                }

                facilityMarkerClickListenerRef.current = facilityMarkerRef.current.addListener("click", () => {
                    const content = document.createElement("div");
                    content.textContent = facilityMarkerRef.current.getTitle();
                    facilityInfoWindowRef.current.close();
                    facilityInfoWindowRef.current.setContent(content);
                    facilityInfoWindowRef.current.open(map, facilityMarkerRef.current);
                });

                facilityPickupMarkerListenersRef.current.forEach(listener => listener.remove());
                facilityPickupMarkerListenersRef.current = [];
                facilityPickupMarkersRef.current.forEach(marker => marker.setMap(null));
                facilityPickupMarkersRef.current = [];

                const markerBounds = new google.maps.LatLngBounds();
                markerBounds.extend(facilityPosition);

                pickupPoints.forEach(point => {
                    const pickupCoordinates = getValidCoordinates(point.latitude, point.longitude);
                    if (!pickupCoordinates) return;

                    const pickupPosition = {
                        lat: pickupCoordinates.latitude,
                        lng: pickupCoordinates.longitude
                    };
                    const pickupTitle = point.pickupPointName || "Pickup Point";
                    const pickupMarker = new google.maps.Marker({
                        map,
                        position: pickupPosition,
                        title: pickupTitle,
                        optimized: false,
                        icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                    });

                    const pickupMarkerClickListener = pickupMarker.addListener("click", () => {
                        const content = document.createElement("div");
                        content.textContent = pickupMarker.getTitle();
                        facilityInfoWindowRef.current.close();
                        facilityInfoWindowRef.current.setContent(content);
                        facilityInfoWindowRef.current.open(map, pickupMarker);
                    });

                    facilityPickupMarkersRef.current.push(pickupMarker);
                    facilityPickupMarkerListenersRef.current.push(pickupMarkerClickListener);
                    markerBounds.extend(pickupPosition);
                });

                if (facilityPickupMarkersRef.current.length) {
                    map.fitBounds(markerBounds);
                    google.maps.event.addListenerOnce(map, "bounds_changed", () => {
                        if (map.getZoom() > facilityMapZoom) {
                            map.setZoom(facilityMapZoom);
                        }
                    });
                }

                google.maps.event.addListenerOnce(map, "idle", () => {
                    if (isMounted) {
                        setFacilityGoogleMapReady(true);
                    }
                });
            })
            .catch(() => {
                if (isMounted) {
                    setFacilityGoogleMapReady(false);
                    setGoogleMapError("Unable to load Google Maps.");
                }
            });

        return () => {
            isMounted = false;
        };
    }, [activeTab, googleMapsApiKey, facilityMapZoom, facilityMapQuery, pickupPoints]);

    useEffect(() => {
        if (!showPickupForm) {
            setPickupGoogleMapReady(false);
            return;
        }

        if (!googleMapsApiKey) return;

        const mapElement = pickupMapCanvasRef.current;
        const facilityCoordinates = getValidCoordinates(form.getFieldValue("latitude"), form.getFieldValue("longitude"));

        if (!mapElement || !facilityCoordinates) return;

        let isMounted = true;

        loadGoogleMapsScript(googleMapsApiKey)
            .then(() => {
                if (!isMounted || !window.google?.maps) return;
                setGoogleMapError("");

                const google = window.google;
                const facilityPosition = {
                    lat: facilityCoordinates.latitude,
                    lng: facilityCoordinates.longitude
                };
                const selectedCoordinates = getValidCoordinates(form.getFieldValue("pickupLatitude"), form.getFieldValue("pickupLongitude")) ?? facilityCoordinates;
                const selectedPosition = {
                    lat: selectedCoordinates.latitude,
                    lng: selectedCoordinates.longitude
                };
                const shouldCreateMap = !pickupGoogleMapRef.current || pickupGoogleMapElementRef.current !== mapElement;

                if (shouldCreateMap) {
                    if (pickupGoogleMapListenerRef.current) {
                        pickupGoogleMapListenerRef.current.remove();
                        pickupGoogleMapListenerRef.current = null;
                    }
                    pickupFacilityMarkerRef.current = null;
                    pickupSelectedMarkerRef.current = null;
                    pickupDistanceLineRef.current = null;
                    pickupGoogleMapElementRef.current = mapElement;
                    setPickupGoogleMapReady(false);

                    pickupGoogleMapRef.current = new google.maps.Map(mapElement, {
                        center: facilityPosition,
                        zoom: pickupMapZoom,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: true
                    });
                } else {
                    pickupGoogleMapRef.current.setCenter(facilityPosition);
                    pickupGoogleMapRef.current.setZoom(pickupMapZoom);
                }

                const map = pickupGoogleMapRef.current;
                google.maps.event.trigger(map, "resize");
                const fitPickupMapBounds = (pickupPosition: { lat: number; lng: number }) => {
                    const bounds = new google.maps.LatLngBounds();
                    bounds.extend(facilityPosition);
                    bounds.extend(pickupPosition);
                    map.fitBounds(bounds);
                    google.maps.event.addListenerOnce(map, "bounds_changed", () => {
                        if (map.getZoom() > pickupMapZoom) {
                            map.setZoom(pickupMapZoom);
                        }
                    });
                };

                if (!pickupFacilityMarkerRef.current) {
                    pickupFacilityMarkerRef.current = new google.maps.Marker({
                        map,
                        title: getFacilityAddressQuery(),
                        icon: "https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                    });
                }

                pickupFacilityMarkerRef.current.setPosition(facilityPosition);

                if (!pickupSelectedMarkerRef.current) {
                    pickupSelectedMarkerRef.current = new google.maps.Marker({
                        map,
                        draggable: true,
                        title: "Pickup Point",
                        icon: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                    });
                }

                pickupSelectedMarkerRef.current.setPosition(selectedPosition);

                if (!pickupDistanceLineRef.current) {
                    pickupDistanceLineRef.current = new google.maps.Polyline({
                        map,
                        strokeColor: "#1677ff",
                        strokeOpacity: 0.8,
                        strokeWeight: 2
                    });
                }

                pickupDistanceLineRef.current.setPath([facilityPosition, selectedPosition]);
                fitPickupMapBounds(selectedPosition);
                google.maps.event.addListenerOnce(map, "idle", () => {
                    if (isMounted) {
                        setPickupGoogleMapReady(true);
                    }
                });

                const updatePickupCoordinates = (lat: number, lng: number) => {
                    form.setFieldsValue({
                        pickupLatitude: Number(lat.toFixed(6)),
                        pickupLongitude: Number(lng.toFixed(6)),
                        pickupPlaceId: form.getFieldValue("placeId")
                    });
                    pickupSelectedMarkerRef.current?.setPosition({ lat, lng });
                    pickupDistanceLineRef.current?.setPath([facilityPosition, { lat, lng }]);
                    fitPickupMapBounds({ lat, lng });
                    setPickupMapRefreshKey(value => value + 1);
                };

                if (pickupGoogleMapListenerRef.current) {
                    pickupGoogleMapListenerRef.current.remove();
                }

                pickupGoogleMapListenerRef.current = map.addListener("click", (event: any) => {
                    const lat = event.latLng?.lat();
                    const lng = event.latLng?.lng();
                    if (typeof lat === "number" && typeof lng === "number") {
                        updatePickupCoordinates(lat, lng);
                    }
                });

                google.maps.event.clearListeners(pickupSelectedMarkerRef.current, "dragend");
                pickupSelectedMarkerRef.current.addListener("dragend", (event: any) => {
                    const lat = event.latLng?.lat();
                    const lng = event.latLng?.lng();
                    if (typeof lat === "number" && typeof lng === "number") {
                        updatePickupCoordinates(lat, lng);
                    }
                });
            })
            .catch(() => {
                if (isMounted) {
                    setGoogleMapError("Unable to load Google Maps.");
                }
            });

        return () => {
            isMounted = false;
        };
    }, [showPickupForm, googleMapsApiKey, pickupMapZoom, pickupMapRefreshKey]);

    const updatePeriod = (dayIndex: number, periodIndex: number, field: keyof BusinessHourPeriod, value: dayjs.Dayjs | null) => {
        setBusinessHours(days => days.map((day, index) => {
            if (index !== dayIndex) return day;
            return {
                ...day,
                periods: day.periods.map((period, pIndex) => pIndex === periodIndex ? { ...period, [field]: value ?? undefined } : period)
            };
        }));
    };

    const isCompletePeriod = (period?: BusinessHourPeriod) => Boolean(period?.open && period?.close);
    const isPartialPeriod = (period: BusinessHourPeriod) => Boolean(period.open || period.close) && !isCompletePeriod(period);
    const canAddPeriod = (day: BusinessHourDay) => !day.periods.length || isCompletePeriod(day.periods[day.periods.length - 1]);
    const hasIncompleteBusinessHours = () => businessHours.some(day => !day.closed && day.periods.some(isPartialPeriod));

    const addPeriod = (dayIndex: number) => {
        setBusinessHours(days => days.map((day, index) => index === dayIndex ? {
            ...day,
            closed: false,
            periods: [...day.periods, {}]
        } : day));
    };

    const removePeriod = (dayIndex: number, periodIndex: number) => {
        setBusinessHours(days => days.map((day, index) => index === dayIndex ? {
            ...day,
            periods: day.periods.filter((_, pIndex) => pIndex !== periodIndex)
        } : day));
    };

    const setClosed = (dayIndex: number, closed: boolean) => {
        setBusinessHours(days => days.map((day, index) => index === dayIndex ? {
            ...day,
            closed,
            periods: closed ? [] : day.periods
        } : day));
    };

    const clearPickupPointForm = () => {
        form.setFieldsValue({
            pickupPointCode: undefined,
            pickupPointName: undefined,
            pickupPointType: undefined,
            pickupSectionName: undefined,
            pickupFloorLevel: undefined,
            pickupLandmark: undefined,
            pickupInstructions: undefined,
            pickupAddress: undefined,
            pickupLatitude: undefined,
            pickupLongitude: undefined,
            pickupPlaceId: undefined,
            pickupIsDefault: false,
            pickupNotes: undefined
        });
        setEditingPickupPointId(null);
        setPickupMapRefreshKey(value => value + 1);
        setPickupGoogleMapReady(false);
    };

    const addPickupPoint = async () => {
        try {
            await form.validateFields(["pickupPointName", "pickupPointType", "pickupLatitude", "pickupLongitude"]);
            const values = form.getFieldsValue();
            const isEditing = editingPickupPointId !== null;
            const pickupPoint: FacilityPickupPoint = {
                id: editingPickupPointId ?? Date.now(),
                pickupPointCode: values.pickupPointCode?.trim() ?? "",
                pickupPointName: values.pickupPointName!,
                pointType: values.pickupPointType!,
                sectionName: values.pickupSectionName,
                floorLevel: values.pickupFloorLevel,
                landmark: values.pickupLandmark,
                instructions: values.pickupInstructions,
                pickupAddress: values.pickupAddress,
                latitude: values.pickupLatitude!,
                longitude: values.pickupLongitude!,
                placeId: values.pickupPlaceId,
                isDefault: values.pickupIsDefault === true,
                notes: values.pickupNotes
            };

            setPickupPoints(points => {
                const normalizedPoints = points.map(point => ({
                    ...point,
                    isDefault: values.pickupIsDefault && point.id !== pickupPoint.id ? false : point.isDefault
                }));

                if (isEditing) {
                    return normalizedPoints.map(point => point.id === editingPickupPointId ? pickupPoint : point);
                }

                return [
                    ...normalizedPoints,
                    pickupPoint
                ];
            });
            clearPickupPointForm();
            setShowPickupForm(false);
            notify("success", isEditing ? "Pickup point updated." : "Pickup point added.");
        } catch {
            notify("warning", "Please fill required pickup point details.");
        }
    };

    const editPickupPoint = (point: FacilityPickupPoint) => {
        setEditingPickupPointId(point.id);
        form.setFieldsValue({
            pickupPointCode: point.pickupPointCode,
            pickupPointName: point.pickupPointName,
            pickupPointType: point.pointType,
            pickupSectionName: point.sectionName,
            pickupFloorLevel: point.floorLevel,
            pickupLandmark: point.landmark,
            pickupInstructions: point.instructions,
            pickupAddress: point.pickupAddress,
            pickupLatitude: point.latitude,
            pickupLongitude: point.longitude,
            pickupPlaceId: point.placeId,
            pickupIsDefault: point.isDefault,
            pickupNotes: point.notes
        });
        setPickupMapRefreshKey(value => value + 1);
        setPickupGoogleMapReady(false);
        setShowPickupForm(true);
    };

    const getFacilityCoordinates = () => ({
        latitude: form.getFieldValue("latitude"),
        longitude: form.getFieldValue("longitude")
    });

    const getValidCoordinates = (latitude?: number | string | null, longitude?: number | string | null) => {
        const parsedLatitude = Number(latitude);
        const parsedLongitude = Number(longitude);

        if (!Number.isFinite(parsedLatitude) || !Number.isFinite(parsedLongitude)) {
            return null;
        }

        return {
            latitude: parsedLatitude,
            longitude: parsedLongitude
        };
    };

    const setPickupLocationFromFacility = (showNotification = true) => {
        const { latitude, longitude } = getFacilityCoordinates();

        form.setFieldsValue({
            pickupLatitude: latitude,
            pickupLongitude: longitude,
            pickupPlaceId: form.getFieldValue("placeId")
        });
        setPickupMapRefreshKey(value => value + 1);

        if (showNotification) {
            notify(latitude && longitude ? "info" : "warning", latitude && longitude
                ? "Facility map location selected."
                : "Facility location is not available.");
        }
    };

    const getPickupPointMapUrl = (point: FacilityPickupPoint) => {
        const coordinates = getValidCoordinates(point.latitude, point.longitude);

        if (coordinates) {
            return `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}&z=17&output=embed`;
        }

        return `https://www.google.com/maps?q=${encodeURIComponent(getMapQuery())}&z=17&output=embed`;
    };

    const getPickupMapFallbackUrl = () => {
        const coordinates = getValidCoordinates(form.getFieldValue("latitude"), form.getFieldValue("longitude"));

        if (coordinates) {
            return `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}&z=${pickupMapZoom}&output=embed`;
        }

        return `https://www.google.com/maps?q=${encodeURIComponent(getMapQuery())}&z=${pickupMapZoom}&output=embed`;
    };

    const getPickupDistanceText = () => {
        const facilityCoordinates = getValidCoordinates(form.getFieldValue("latitude"), form.getFieldValue("longitude"));
        const pickupCoordinates = getValidCoordinates(form.getFieldValue("pickupLatitude"), form.getFieldValue("pickupLongitude"));

        if (!facilityCoordinates || !pickupCoordinates) {
            return "";
        }

        const toRadians = (value: number) => value * Math.PI / 180;
        const earthRadiusMiles = 3958.8;
        const latitudeDelta = toRadians(pickupCoordinates.latitude - facilityCoordinates.latitude);
        const longitudeDelta = toRadians(pickupCoordinates.longitude - facilityCoordinates.longitude);
        const startLatitude = toRadians(facilityCoordinates.latitude);
        const endLatitude = toRadians(pickupCoordinates.latitude);
        const haversine =
            Math.sin(latitudeDelta / 2) ** 2
            + Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2;
        const distance = earthRadiusMiles * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));

        if (distance < 0.1) {
            return `Distance: ${Math.round(distance * 5280)} ft`;
        }

        return `Distance: ${distance.toFixed(1)} mi`;
    };

    const syncMailingAddress = () => {
        const values = form.getFieldsValue();
        form.setFieldsValue({
            mailingAddressLine1: values.addressLine1,
            mailingAddressLine2: values.addressLine2,
            mailingCity: values.city,
            mailingStateCode: values.stateCode,
            mailingZipcode: values.zipcode,
            mailingCounty: values.county,
            mailingCountryCode: values.countryCode ?? "US"
        });
    };

    const handleSameAsPhysicalAddress = (checked: boolean) => {
        setIsSameAsPhysicalAddress(checked);

        if (checked) {
            syncMailingAddress();
        }
    };

    const handleFormValuesChange = (changedValues: Partial<FacilityFormValues>) => {
        if (changedValues.facilityName !== undefined) {
            setFacilityMapQuery(changedValues.facilityName ?? "");
            if (!changedValues.facilityName) {
                setIsPhysicalAddressLocked(false);
                setGeocodeResponseJson("");
                setPlaceDetailsResponseJson("");
                setDestinationAPiResponseJson("");
            }
        }

        if (changedValues.addressLine1 !== undefined && !isPhysicalAddressLocked) {
            form.setFieldsValue({
                addressLine2: undefined,
                city: undefined,
                stateCode: undefined,
                zipcode: undefined,
                county: undefined,
                latitude: undefined,
                longitude: undefined,
                placeId: undefined
            });
            setFacilityMapQuery(changedValues.addressLine1 ?? "");
            setBusinessHours(initialBusinessHours);
            setGeocodeResponseJson("");
            setPlaceDetailsResponseJson("");
            setDestinationAPiResponseJson("");
        }

        if (changedValues.isSameAsPhysicalAddress !== undefined) {
            setIsSameAsPhysicalAddress(changedValues.isSameAsPhysicalAddress);
        }

        if (
            form.getFieldValue("isSameAsPhysicalAddress")
            && ["addressLine1", "addressLine2", "city", "stateCode", "zipcode", "county", "countryCode"].some(field => field in changedValues)
        ) {
            syncMailingAddress();
        }
    };

    const getMapQuery = () => {
        const addressParts = [
            form.getFieldValue("addressLine1"),
            form.getFieldValue("city"),
            form.getFieldValue("stateCode"),
            form.getFieldValue("zipcode")
        ].filter(Boolean).join(", ");

        return facilityMapQuery || addressParts || "United States";
    };

    const getFacilityAddressQuery = () => {
        const addressParts = [
            form.getFieldValue("addressLine1"),
            form.getFieldValue("addressLine2"),
            form.getFieldValue("city"),
            form.getFieldValue("stateCode"),
            form.getFieldValue("zipcode")
        ].filter(Boolean);

        return addressParts.join(", ") || getMapQuery();
    };

    const getFacilityAddressText = () => {
        const addressParts = [
            form.getFieldValue("facilityName"),
            form.getFieldValue("addressLine1"),
            form.getFieldValue("addressLine2"),
            form.getFieldValue("city"),
            form.getFieldValue("stateCode"),
            form.getFieldValue("zipcode")
        ].filter(Boolean);

        return addressParts.join(", ") || getMapQuery();
    };

    const getFacilityMapUrl = () => {
        const coordinates = getValidCoordinates(form.getFieldValue("latitude"), form.getFieldValue("longitude"));
        const addressQuery = getFacilityAddressQuery();

        if (coordinates) {
            return `https://www.google.com/maps?q=${coordinates.latitude},${coordinates.longitude}&z=${facilityMapZoom}&output=embed`;
        }

        if (addressQuery) {
            return `https://www.google.com/maps?q=${encodeURIComponent(addressQuery)}&z=${facilityMapZoom}&output=embed`;
        }

        return `https://www.google.com/maps?q=United%20States&z=${facilityMapZoom}&output=embed`;
    };

    const zoomFacilityMap = (direction: "in" | "out") => {
        setFacilityMapZoom(currentZoom => {
            const nextZoom = direction === "in" ? currentZoom + 1 : currentZoom - 1;
            return Math.min(Math.max(nextZoom, 4), 21);
        });
    };

    const getTabFields = (tabKey: string) => {
        if (tabKey === "facility-address") {
            return ["facilityName", "facilityServiceType", "addressLine1", "city", "stateCode", "zipcode", "county", "countryCode", "latitude", "longitude"];
        }

        if (tabKey === "pickup-points" && showPickupForm) {
            return ["pickupPointName", "pickupPointType", "pickupLatitude", "pickupLongitude"];
        }

        if (tabKey === "contact-hours") {
            return ["mainPhone", "alternatePhone", "fax", "email", "timeZoneId"];
        }

        if (tabKey === "treatment-notes") {
            return [];
        }

        if (tabKey === "google-data") {
            return [];
        }

        return [];
    };

    const validateCurrentTab = async () => {
        const fields = getTabFields(activeTab);
        if (fields.length) {
            await form.validateFields(fields as any);
        }
    };

    const goToNextTab = async () => {
        if (activeTab === "contact-hours" && hasIncompleteBusinessHours()) {
            notify("warning", "Please select both start time and end time for working hours.");
            return;
        }

        try {
            await validateCurrentTab();
            setActiveTab(tabKeys[Math.min(currentTabIndex + 1, tabKeys.length - 1)]);
        } catch {
            notify("warning", "Please fill required details.");
        }
    };

    const saveFacility = async () => {
        if (hasIncompleteBusinessHours()) {
            notify("warning", "Please select both start time and end time for working hours.");
            return;
        }

        try {
            await form.validateFields([
                ...getTabFields("facility-address"),
                ...getTabFields("contact-hours"),
                ...getTabFields("treatment-notes")
            ] as any);
            submit(form.getFieldsValue() as FacilityFormValues);
        } catch {
            notify("warning", "Please fill required details.");
        }
    };

    const addClientTreatmentRow = () => {
        setClientTreatmentRows(rows => [
            ...rows,
            createDefaultClientTreatmentRow()
        ]);
    };

    const updateClientTreatmentRow = (rowId: number, updates: Partial<ClientTreatmentTypeRow>) => {
        setClientTreatmentRows(rows => rows.map(row => row.id === rowId ? { ...row, ...updates } : row));
    };

    const removeClientTreatmentRow = (rowId: number) => {
        setClientTreatmentRows(rows => rows.filter(row => row.id !== rowId));
    };

    const changeTab = async (nextTab: string) => {
        if (nextTab === "google-data") {
            setActiveTab(nextTab);
            return;
        }

        if (tabKeys.indexOf(nextTab) <= currentTabIndex) {
            setActiveTab(nextTab);
            return;
        }

        try {
            await validateCurrentTab();
            setActiveTab(nextTab);
        } catch {
            notify("warning", "Please fill required details.");
        }
    };

    const mapPlaceOptions = (predictions: any[]): FacilityPlaceOption[] => predictions.map((item: any) => {
        const description = item.description ?? item.Description ?? "";

        return {
            value: description,
            label: <span className="facility-suggestion-label">{description}</span>,
            placeId: item.placeId ?? item.PlaceId
        };
    }).filter((option: FacilityPlaceOption) => option.value);

    const searchFacilityPlaces = (value: string) => {
        window.clearTimeout(facilityNameSearchRef.current);

        if (!value || value.trim().length < 3) {
            setFacilityPlaceOptions([]);
            setFacilityPlaceLoading(false);
            return;
        }

        setFacilityPlaceLoading(true);
        facilityNameSearchRef.current = window.setTimeout(() => {
            getAddressSuggestionsV2(value.trim())
                .then(response => {
                    const predictions = response?.predictions ?? response?.Predictions ?? [];
                    setFacilityPlaceOptions(mapPlaceOptions(predictions));
                })
                .catch(() => notify("warning", "Unable to load facility suggestions."))
                .finally(() => setFacilityPlaceLoading(false));
        }, 350);
    };

    const searchPhysicalAddress = (value: string) => {
        window.clearTimeout(physicalAddressSearchRef.current);

        if (isPhysicalAddressLocked || !/^\d+\s+[A-Za-z]/.test(value.trim())) {
            setPhysicalAddressOptions([]);
            setPhysicalAddressLoading(false);
            return;
        }

        setPhysicalAddressLoading(true);
        physicalAddressSearchRef.current = window.setTimeout(() => {
            getAddressSuggestionsV2(value.trim())
                .then(response => {
                    const predictions = response?.predictions ?? response?.Predictions ?? [];
                    setPhysicalAddressOptions(mapPlaceOptions(predictions));
                })
                .catch(() => notify("warning", "Unable to load address suggestions."))
                .finally(() => setPhysicalAddressLoading(false));
        }, 350);
    };

    const getPlaceDetailValue = <T,>(details: any, camelKey: string, pascalKey: string): T | undefined =>
        details?.[camelKey] ?? details?.[pascalKey];

    const bindFacilityPlaceDetails = (details: any, selectedName: string, updateFacilityName = true) => {
        if (!details) return;

        const normalized: FacilityPlaceDetails = {
            placeId: getPlaceDetailValue(details, "placeId", "PlaceId"),
            facilityName: getPlaceDetailValue(details, "facilityName", "FacilityName"),
            formattedAddress: getPlaceDetailValue(details, "formattedAddress", "FormattedAddress"),
            addressLine1: getPlaceDetailValue(details, "addressLine1", "AddressLine1"),
            addressLine2: getPlaceDetailValue(details, "addressLine2", "AddressLine2"),
            city: getPlaceDetailValue(details, "city", "City"),
            stateCode: getPlaceDetailValue(details, "stateCode", "StateCode"),
            county: getPlaceDetailValue(details, "county", "County"),
            zipcode: getPlaceDetailValue(details, "zipcode", "Zipcode"),
            countryCode: getPlaceDetailValue(details, "countryCode", "CountryCode"),
            latitude: getPlaceDetailValue(details, "latitude", "Latitude"),
            longitude: getPlaceDetailValue(details, "longitude", "Longitude"),
            mainPhone: getPlaceDetailValue(details, "mainPhone", "MainPhone"),
            timeZoneId: getPlaceDetailValue(details, "timeZoneId", "TimeZoneId"),
            geocodeResponseJson: getPlaceDetailValue(details, "geocodeResponseJson", "GeocodeResponseJson"),
            placeDetailsResponseJson: getPlaceDetailValue(details, "placeDetailsResponseJson", "PlaceDetailsResponseJson"),
            destinationAPiResponseJson: getPlaceDetailValue(details, "destinationAPiResponseJson", "DestinationAPiResponseJson"),
            weekdayDescriptions: getPlaceDetailValue(details, "weekdayDescriptions", "WeekdayDescriptions")
        };

        form.setFieldsValue({
            facilityName: updateFacilityName ? (normalized.facilityName || selectedName) : selectedName,
            addressLine1: normalized.addressLine1,
            addressLine2: normalized.addressLine2,
            city: normalized.city,
            stateCode: normalized.stateCode,
            zipcode: normalized.zipcode,
            county: normalized.county,
            countryCode: normalized.countryCode || "US",
            latitude: normalized.latitude,
            longitude: normalized.longitude,
            placeId: normalized.placeId,
            mainPhone: normalized.mainPhone,
            timeZoneId: normalized.timeZoneId
        });
        setGeocodeResponseJson(normalized.geocodeResponseJson ?? "");
        setPlaceDetailsResponseJson(normalized.placeDetailsResponseJson ?? "");
        const nextDestinationJson = normalized.destinationAPiResponseJson ?? normalized.placeDetailsResponseJson ?? "";
        setDestinationAPiResponseJson(nextDestinationJson);
        const navigationPickupPoints = buildNavigationPickupPoints(nextDestinationJson);
        if (navigationPickupPoints.length) {
            setPickupPoints(navigationPickupPoints);
        }
        setFacilityMapQuery(normalized.formattedAddress || normalized.facilityName || selectedName);
        const parsedWorkingHours = parseGoogleWeekdayDescriptions(normalized.weekdayDescriptions);
        if (parsedWorkingHours) {
            setBusinessHours(parsedWorkingHours);
        }

        if (form.getFieldValue("isSameAsPhysicalAddress")) {
            window.setTimeout(syncMailingAddress);
        }
    };

    const selectFacilityPlace = (value: string, option: FacilityPlaceOption) => {
        form.setFieldsValue({
            facilityName: value,
            placeId: option.placeId
        });
        setFacilityMapQuery(value);
        setFacilityPlaceLoading(true);
        getFacilityPlaceDetails(value, option.placeId)
            .then(response => {
                bindFacilityPlaceDetails(response, value);
                setIsPhysicalAddressLocked(true);
            })
            .catch(() => notify("warning", "Unable to validate facility location."))
            .finally(() => setFacilityPlaceLoading(false));
    };

    const selectPhysicalAddress = (value: string, option: FacilityPlaceOption) => {
        setFacilityMapQuery(value);
        setPhysicalAddressLoading(true);
        getFacilityPlaceDetails(value, option.placeId)
            .then(response => {
                bindFacilityPlaceDetails(response, form.getFieldValue("facilityName") ?? "", false);
                setIsPhysicalAddressLocked(true);
            })
            .catch(() => notify("warning", "Unable to validate address."))
            .finally(() => setPhysicalAddressLoading(false));
    };

    const submit = (values: FacilityFormValues) => {
        const clientTreatmentTypes = clientTreatmentRows
            .map(row => ({
                clientId: Number(row.clientId),
                treatmentTypes: row.treatmentTypes
                    .map(treatmentType => treatmentType.trim())
                    .filter(Boolean),
                actions: row.actions
                    .map(action => action.trim())
                    .filter(Boolean)
            }))
            .filter(row => Number.isFinite(row.clientId) && row.clientId > 0);
        const mailingAddressValues = values.isSameAsPhysicalAddress ? {
            mailingAddressLine1: values.addressLine1?.trim(),
            mailingAddressLine2: values.addressLine2?.trim(),
            mailingCity: values.city?.trim(),
            mailingStateCode: values.stateCode,
            mailingZipcode: values.zipcode?.trim(),
            mailingCounty: values.county,
            mailingCountryCode: values.countryCode ?? "US"
        } : {
            mailingAddressLine1: values.mailingAddressLine1?.trim(),
            mailingAddressLine2: values.mailingAddressLine2?.trim(),
            mailingCity: values.mailingCity?.trim(),
            mailingStateCode: values.mailingStateCode,
            mailingZipcode: values.mailingZipcode?.trim(),
            mailingCounty: values.mailingCounty,
            mailingCountryCode: values.mailingCountryCode ?? "US"
        };

        onSaved({
            id: facility?.id ?? Date.now(),
            facilityName: values.facilityName.trim(),
            facilityServiceType: values.facilityServiceType,
            isActive: values.isActive,
            addressLine1: values.addressLine1?.trim() ?? "",
            addressLine2: values.addressLine2?.trim(),
            city: values.city?.trim() ?? "",
            stateCode: values.stateCode,
            zipcode: values.zipcode?.trim() ?? "",
            county: values.county,
            countryCode: values.countryCode ?? "US",
            isSameAsPhysicalAddress: values.isSameAsPhysicalAddress,
            ...mailingAddressValues,
            latitude: values.latitude,
            longitude: values.longitude,
            placeId: values.placeId,
            mainPhone: values.mainPhone,
            alternatePhone: values.alternatePhone,
            fax: values.fax,
            email: values.email,
            timeZoneId: values.timeZoneId,
            businessHoursJson: serializeBusinessHours(businessHours),
            geocodeResponseJson,
            placeDetailsResponseJson,
            destinationAPiResponseJson,
            treatmentTypes: values.treatmentTypes,
            notes: values.notes,
            tags: values.tags,
            pickupPoints,
            clientTreatmentTypes
        });
    };

    return (
        <>
            <Form<FacilityFormValues>
                form={form}
                className="facility-form"
                layout="vertical"
                requiredMark
                initialValues={initialValues}
                onValuesChange={handleFormValuesChange}
                onFinish={values => {
                    if (activeTab === "treatment-notes") {
                        submit(values);
                    }
                }}
                onFinishFailed={() => notify("warning", "Please fill required details.")}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={changeTab}
                    type="card"
                    style={{ width: "100%" }}
                    tabBarStyle={{ marginBottom: 0 }}
                    items={[
                        {
                            key: "facility-address",
                            label: "Facility / Address Info",
                            children: (
                                <div className="facility-form-section">
                                    <div className="facility-card">
                                        <div className="facility-card-title">Facility Information</div>
                                        <Row gutter={16}>
                                            <Col xs={24} md={8}>
                                                <Row gutter={16}>
                                                    <Col span={24}>
                                                        <Form.Item name="facilityName" label="Facility Name" rules={[{ required: true, whitespace: true }, { max: 100 }]}>
                                                            <AutoComplete
                                                                options={facilityPlaceOptions}
                                                                onSearch={searchFacilityPlaces}
                                                                onSelect={(value, option) => selectFacilityPlace(value, option as FacilityPlaceOption)}
                                                                notFoundContent={facilityPlaceLoading ? "Searching..." : null}
                                                            >
                                                                <Input maxLength={100} placeholder="Enter Facility Name" />
                                                            </AutoComplete>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={24}>
                                                        <Form.Item name="facilityServiceType" label="Facility Service Type" rules={[{ required: true, message: "Facility Service Type is required." }]}>
                                                            <Select mode="multiple" loading={lookupLoading} allowClear placeholder="Select Service Type" options={facilityServiceTypeOptions} />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={24}>
                                                        <Form.Item name="isActive" label="Status" rules={[{ required: true, message: "Status is required." }]}>
                                                            <Radio.Group>
                                                                <Radio value={true}>Active</Radio>
                                                                <Radio value={false}>Inactive</Radio>
                                                            </Radio.Group>
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col xs={24} md={16}>
                                                <Form.Item shouldUpdate noStyle>
                                                    {() => (
                                                        <div className="facility-inline-map">
                                                            <iframe
                                                                title="Facility map"
                                                                src={getFacilityMapUrl()}
                                                                loading="lazy"
                                                            />
                                                            {googleMapsApiKey && !googleMapError && (
                                                                <div
                                                                    ref={facilityMapCanvasRef}
                                                                    className={`facility-google-map-canvas facility-google-map-canvas-inline${facilityGoogleMapReady ? " ready" : ""}`}
                                                                />
                                                            )}
                                                            <div className="facility-map-zoom-controls">
                                                                <Tooltip title="Zoom in">
                                                                    <Button
                                                                        size="small"
                                                                        icon={<ZoomInOutlined />}
                                                                        onClick={() => zoomFacilityMap("in")}
                                                                    />
                                                                </Tooltip>
                                                                <Tooltip title="Zoom out">
                                                                    <Button
                                                                        size="small"
                                                                        icon={<ZoomOutOutlined />}
                                                                        onClick={() => zoomFacilityMap("out")}
                                                                    />
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                    <div className="facility-card">
                                        <div className="facility-card-title">Physical Address</div>
                                        <Row gutter={16}>
                                            <Col xs={24} md={8}>
                                                <Form.Item
                                                    name="addressLine1"
                                                    label="Address Line 1"
                                                    rules={[
                                                        { required: true, whitespace: true, message: "Address Line 1 is required." },
                                                        { max: 150 }
                                                    ]}
                                                >
                                                    <AutoComplete
                                                        options={physicalAddressOptions}
                                                        onSearch={searchPhysicalAddress}
                                                        onSelect={(value, option) => selectPhysicalAddress(value, option as FacilityPlaceOption)}
                                                        notFoundContent={physicalAddressLoading ? "Searching..." : null}
                                                        disabled={isPhysicalAddressLocked}
                                                    >
                                                        <Input disabled={isPhysicalAddressLocked} maxLength={150} placeholder="Enter Address Line 1" />
                                                    </AutoComplete>
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="addressLine2" label="Address Line 2" rules={[{ max: 150 }]}>
                                                    <Input maxLength={150} placeholder="Address Line 2" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="city" label="City" rules={[{ required: true, whitespace: true }, { max: 100 }]}>
                                                    <Input disabled maxLength={100} placeholder="City" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="stateCode" label="State" rules={[{ required: true }, { max: 10 }]}>
                                                    <Select loading={lookupLoading} disabled allowClear showSearch optionFilterProp="label" placeholder="State" options={stateOptions} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="zipcode" label="Zipcode" rules={zipCodeRules}>
                                                    <Input disabled maxLength={10} placeholder="Zipcode" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="county" label="County" rules={[{ required: true, message: "County is required." }, { max: 100 }]}>
                                                    <Select disabled allowClear showSearch optionFilterProp="label" placeholder="County" options={countyOptions} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="countryCode" label="Country Code" rules={[{ required: true, message: "Country Code is required." }]}>
                                                    <Input disabled maxLength={10} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="latitude" label="Latitude" rules={[{ required: true, message: "Latitude is required." }]}>
                                                    <InputNumber disabled precision={6} style={{ width: "100%" }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="longitude" label="Longitude" rules={[{ required: true, message: "Longitude is required." }]}>
                                                    <InputNumber disabled precision={6} style={{ width: "100%" }} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="placeId" label="Place Id">
                                                    <Input disabled />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                    <div className="facility-card">
                                        <Flex justify="space-between" align="center" className="facility-card-header">
                                            <span>Mailing Address</span>
                                            <Form.Item name="isSameAsPhysicalAddress" valuePropName="checked" noStyle>
                                                <Checkbox onChange={event => handleSameAsPhysicalAddress(event.target.checked)}>
                                                    Is Same as Physical Address
                                                </Checkbox>
                                            </Form.Item>
                                        </Flex>
                                        <Row gutter={16}>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="mailingAddressLine1" label="Address Line 1" rules={[{ max: 150 }]}>
                                                    <Input disabled={isSameAsPhysicalAddress} maxLength={150} placeholder="Enter Address Line 1" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="mailingAddressLine2" label="Address Line 2" rules={[{ max: 150 }]}>
                                                    <Input disabled={isSameAsPhysicalAddress} maxLength={150} placeholder="Enter Address Line 2" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="mailingCity" label="City" rules={[{ max: 100 }]}>
                                                    <Input disabled={isSameAsPhysicalAddress} maxLength={100} placeholder="Enter City" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="mailingStateCode" label="State" rules={[{ max: 10 }]}>
                                                    <Select loading={lookupLoading} disabled={isSameAsPhysicalAddress} allowClear showSearch optionFilterProp="label" placeholder="Select State" options={stateOptions} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="mailingZipcode" label="Zipcode" rules={[{ max: 20 }]}>
                                                    <Input disabled={isSameAsPhysicalAddress} maxLength={20} placeholder="Enter Zipcode" />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="mailingCounty" label="County" rules={[{ max: 100 }]}>
                                                    <Select disabled={isSameAsPhysicalAddress} allowClear showSearch optionFilterProp="label" placeholder="Select County" options={countyOptions} />
                                                </Form.Item>
                                            </Col>
                                            <Col xs={24} md={8}>
                                                <Form.Item name="mailingCountryCode" label="Country Code">
                                                    <Input disabled maxLength={10} />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: "pickup-points",
                            label: "Pickup Points",
                            children: (
                                <div className="facility-form-section">
                                    <div className="facility-card">
                                    {!showPickupForm ? (
                                        <>
                                            <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                                                <strong>Selected Pickup Points</strong>
                                                <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                                                    clearPickupPointForm();
                                                    setPickupLocationFromFacility(false);
                                                    setShowPickupForm(true);
                                                }}>
                                                    Add New
                                                </Button>
                                            </Flex>
                                            <div className="facility-pickup-card-list">
                                                {pickupPoints.length ? pickupPoints.map(point => (
                                                    <div className="facility-pickup-card" key={point.id}>
                                                        <div className="facility-pickup-card-layout">
                                                            <PickupPointMapPreview
                                                                apiKey={googleMapsApiKey}
                                                                facilityLatitude={form.getFieldValue("latitude")}
                                                                facilityLongitude={form.getFieldValue("longitude")}
                                                                point={point}
                                                                fallbackMapUrl={getPickupPointMapUrl(point)}
                                                            />
                                                            <Flex vertical gap={8} className="facility-pickup-card-summary">
                                                                <Flex justify="space-between" align="flex-start" gap="middle">
                                                                    <div>
                                                                        <div className="facility-pickup-card-title">{point.pickupPointName}</div>
                                                                        <div className="facility-pickup-card-code">{point.pickupPointCode}</div>
                                                                    </div>
                                                                    <Space>
                                                                        <Tooltip title="Edit">
                                                                            <Button size="small" icon={<EditOutlined />} onClick={() => editPickupPoint(point)} />
                                                                        </Tooltip>
                                                                        <Tooltip title="Delete">
                                                                            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setPickupPoints(points => points.filter(item => item.id !== point.id))} />
                                                                        </Tooltip>
                                                                    </Space>
                                                                </Flex>
                                                                <div className="facility-pickup-card-subtitle">{point.pointType}</div>
                                                                {point.isDefault && <span className="facility-pickup-default-text">Default</span>}
                                                            </Flex>
                                                        </div>
                                                    </div>
                                                )) : (
                                                    <div className="facility-empty-card">No pickup points selected.</div>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                                                <strong>{editingPickupPointId !== null ? "Edit Pickup Point" : "Add Pickup Point"}</strong>
                                            </Flex>
                                            <Row gutter={16}>
                                                <Col xs={24} lg={12}>
                                                    <Form.Item shouldUpdate noStyle>
                                                        {() => (
                                                            <div className="facility-map-panel">
                                                                <iframe
                                                                    title="Pickup point facility map"
                                                                    src={getPickupMapFallbackUrl()}
                                                                    loading="lazy"
                                                                />
                                                                {googleMapsApiKey && !googleMapError && (
                                                                    <div
                                                                        ref={pickupMapCanvasRef}
                                                                        className={`facility-google-map-canvas${pickupGoogleMapReady ? " ready" : ""}`}
                                                                    />
                                                                )}
                                                                {googleMapError && <div className="facility-map-error">{googleMapError}</div>}
                                                                <div className="facility-pickup-map-legend">
                                                                    <span><i className="facility-map-pin facility-map-pin-red" />Facility</span>
                                                                    <span><i className="facility-map-pin facility-map-pin-blue" />Pickup Point</span>
                                                                    {getPickupDistanceText() && <strong>{getPickupDistanceText()}</strong>}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} lg={12}>
                                                    <Row gutter={16}>
                                                        <Col xs={24} md={12}><Form.Item name="pickupPointName" label="Pickup Point Name" rules={[{ required: true, whitespace: true }, { max: 200 }]}><Input maxLength={200} placeholder="Enter Pickup Point Name" /></Form.Item></Col>
                                                        <Col xs={24} md={12}><Form.Item name="pickupPointCode" label="Pickup Point Code" rules={[{ max: 50 }]}><Input maxLength={50} placeholder="Enter Pickup Point Code" /></Form.Item></Col>
                                                        <Col xs={24} md={12}><Form.Item name="pickupPointType" label="Point Type" rules={[{ required: true }]}><Select placeholder="Select Point Type" options={pointTypeOptions} /></Form.Item></Col>
                                                        <Col xs={24} md={12}><Form.Item name="pickupSectionName" label="Section Name" rules={[{ max: 100 }]}><Input maxLength={100} placeholder="Enter Section Name" /></Form.Item></Col>
                                                        <Col xs={24} md={12}><Form.Item name="pickupFloorLevel" label="Floor Level" rules={[{ max: 50 }]}><Input maxLength={50} placeholder="Enter Floor Level" /></Form.Item></Col>
                                                        <Col xs={24} md={12}><Form.Item name="pickupLandmark" label="Landmark" rules={[{ max: 200 }]}><Input maxLength={200} placeholder="Enter Landmark" /></Form.Item></Col>
                                                        <Col xs={24} md={12}><Form.Item name="pickupLatitude" label="Latitude" rules={[{ required: true }]}><InputNumber disabled precision={6} style={{ width: "100%" }} /></Form.Item></Col>
                                                        <Col xs={24} md={12}><Form.Item name="pickupLongitude" label="Longitude" rules={[{ required: true }]}><InputNumber disabled precision={6} style={{ width: "100%" }} /></Form.Item></Col>
                                                        <Col span={24}><Form.Item name="pickupIsDefault" valuePropName="checked"><Checkbox>Default Pickup Point</Checkbox></Form.Item></Col>
                                                        <Col span={24}><Form.Item name="pickupInstructions" label="Instructions" rules={[{ max: 1000 }]}><Input.TextArea rows={3} maxLength={1000} showCount placeholder="Enter Instructions" /></Form.Item></Col>
                                                        <Col span={24}><Form.Item name="pickupNotes" label="Notes" rules={[{ max: 1000 }]}><Input.TextArea rows={3} maxLength={1000} showCount placeholder="Enter Notes" /></Form.Item></Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: "contact-hours",
                            label: "Contact / Hours",
                            children: (
                                <div className="facility-form-section">
                                    <div className="facility-card">
                                        <div className="facility-card-title">Contact Information</div>
                                    <Row gutter={16}>
                                        <Col xs={24} md={12}><Form.Item name="mainPhone" label="Main Phone" rules={optionalUsPhoneRules} getValueFromEvent={event => formatPhoneNumber(event.target.value)}><Input maxLength={14} inputMode="numeric" placeholder="(999) 000-0000" /></Form.Item></Col>
                                        <Col xs={24} md={12}><Form.Item name="alternatePhone" label="Alternate Phone" rules={optionalUsPhoneRules} getValueFromEvent={event => formatPhoneNumber(event.target.value)}><Input maxLength={14} inputMode="numeric" placeholder="(999) 000-0000" /></Form.Item></Col>
                                        <Col xs={24} md={12}><Form.Item name="fax" label="Fax" rules={optionalUsPhoneRules} getValueFromEvent={event => formatPhoneNumber(event.target.value)}><Input maxLength={14} inputMode="numeric" placeholder="(999) 000-0000" /></Form.Item></Col>
                                        <Col xs={24} md={12}><Form.Item name="email" label="Email" rules={[{ type: "email", message: "Email must be valid." }, { max: 100 }]}><Input maxLength={100} placeholder="Enter Email" /></Form.Item></Col>
                                        <Col span={24}><Form.Item name="timeZoneId" label="Time Zone" rules={[{ required: true, message: "Time Zone is required." }]}><Select placeholder="Select Time Zone" options={timeZoneOptions} /></Form.Item></Col>
                                    </Row>
                                    </div>
                                    <div className="facility-card">
                                    <div className="facility-card-title">Working Hours</div>
                                    <div className="facility-hours-table">
                                        {businessHours.map((day, dayIndex) => (
                                            <div className="facility-hours-row" key={day.day}>
                                                <div className="facility-hours-day">{day.day}</div>
                                                <div className="facility-hours-periods">
                                                    {day.closed ? (
                                                        <span className="facility-closed-text">Closed</span>
                                                    ) : day.periods.map((period, periodIndex) => (
                                                        <Space key={`${day.day}-${periodIndex}`} style={{ marginBottom: 6 }}>
                                                            <TimePicker use12Hours format="hh:mm A" status={period.close && !period.open ? "error" : undefined} value={period.open} onChange={value => updatePeriod(dayIndex, periodIndex, "open", value)} />
                                                            <span>to</span>
                                                            <TimePicker use12Hours format="hh:mm A" status={period.open && !period.close ? "error" : undefined} value={period.close} onChange={value => updatePeriod(dayIndex, periodIndex, "close", value)} />
                                                            {periodIndex > 0 && <Button danger icon={<DeleteOutlined />} onClick={() => removePeriod(dayIndex, periodIndex)} />}
                                                        </Space>
                                                    ))}
                                                </div>
                                                <Checkbox checked={day.closed} onChange={event => setClosed(dayIndex, event.target.checked)}>Closed</Checkbox>
                                                {!day.closed && <Button type="link" icon={<PlusOutlined />} disabled={!canAddPeriod(day)} onClick={() => addPeriod(dayIndex)}>Add Period</Button>}
                                            </div>
                                        ))}
                                    </div>
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: "treatment-notes",
                            label: "Treatment Type / Notes",
                            children: (
                                <div className="facility-form-section">
                                    <div className="facility-card">
                                        <div className="facility-card-title">Treatment Type / Notes</div>
                                    <Row gutter={16}>
                                        <Col span={24}>
                                            <Form.Item name="treatmentTypes" label="Treatment Type">
                                                <Select mode="multiple" loading={lookupLoading} allowClear placeholder="Select Treatment Type" options={treatmentTypeOptions} />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="notes" label="Notes" rules={[{ max: 2000 }]}>
                                                <Input.TextArea rows={7} maxLength={2000} showCount placeholder="Enter Facility Notes" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item name="tags" label="Tags">
                                                <Select
                                                    mode="tags"
                                                    allowClear
                                                    showSearch
                                                    loading={lookupLoading}
                                                    placeholder="Select or type tags"
                                                    optionFilterProp="label"
                                                    options={facilityTagOptions}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <div className="facility-client-treatment-section">
                                        <Flex align="center" justify="space-between" gap="small" className="facility-client-treatment-title">
                                            <div className="facility-card-title">Client Treatment Types</div>
                                            <Button type="link" icon={<PlusOutlined />} onClick={addClientTreatmentRow}>Add</Button>
                                        </Flex>
                                        <div className="facility-client-treatment-grid">
                                            <div className="facility-client-treatment-header">Client</div>
                                            <div className="facility-client-treatment-header">Treatment Type</div>
                                            <div className="facility-client-treatment-header">Action</div>
                                            <div className="facility-client-treatment-header" />
                                            {clientTreatmentRows.length ? clientTreatmentRows.map(row => (
                                                <div className="facility-client-treatment-row" key={row.id}>
                                                    <Select
                                                        allowClear
                                                        showSearch
                                                        loading={lookupLoading}
                                                        placeholder="Select Client"
                                                        optionFilterProp="label"
                                                        options={clientOptions}
                                                        value={row.clientId}
                                                        onChange={value => updateClientTreatmentRow(row.id, { clientId: value })}
                                                        notFoundContent={lookupLoading ? "Loading clients..." : "No clients found"}
                                                    />
                                                    <Select
                                                        mode="multiple"
                                                        allowClear
                                                        loading={lookupLoading}
                                                        placeholder="Select Treatment Type"
                                                        options={treatmentTypeOptions}
                                                        value={row.treatmentTypes}
                                                        onChange={value => updateClientTreatmentRow(row.id, { treatmentTypes: value })}
                                                    />
                                                    <Select
                                                        mode="multiple"
                                                        allowClear
                                                        placeholder="Select Action"
                                                        options={clientTreatmentActionOptions}
                                                        value={row.actions}
                                                        onChange={value => updateClientTreatmentRow(row.id, { actions: value })}
                                                    />
                                                    <Button
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => removeClientTreatmentRow(row.id)}
                                                    />
                                                </div>
                                            )) : (
                                                <div className="facility-client-treatment-empty">Click Add to map a client to treatment types.</div>
                                            )}
                                        </div>
                                    </div>
                                    </div>
                                </div>
                            )
                        },
                        {
                            key: "google-data",
                            label: "Google Data",
                            children: (
                                <div className="facility-form-section">
                                    <div className="facility-card">
                                        <div className="facility-card-title">Google API Response Data</div>
                                        <div className="facility-json-layout">
                                            <FacilityJsonResponsePanel
                                                title="Geocode Response"
                                                subtitle="Location matching, coordinates, and the Google place identifier captured for this facility."
                                                json={geocodeResponseJson}
                                                previewBuilder={buildGeocodePreview}
                                            />
                                            <FacilityJsonResponsePanel
                                                title="Place Details Response"
                                                subtitle="Facility profile details returned by Google Places, including contact, address, hours, and timezone data."
                                                json={getPlaceDetailsJsonWithDestination(placeDetailsResponseJson, destinationAPiResponseJson)}
                                                previewBuilder={buildPlaceDetailsPreview}
                                            >
                                                {parsed => <PlaceDetailsExtras parsed={parsed} savedBusinessHoursJson={facility?.businessHoursJson} destinationJson={destinationAPiResponseJson} />}
                                            </FacilityJsonResponsePanel>
                                            <FacilityJsonResponsePanel
                                                title="Address Data"
                                                subtitle="Normalized facility address values stored from the selected Google address."
                                                json={facility?.addressDataJson}
                                                previewBuilder={buildAddressDataPreview}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )
                        }
                    ]}
                />
                {showPickupForm ? (
                    <Flex justify="end" gap="small" className="facility-form-actions">
                        <Button icon={<LeftOutlined />} onClick={() => {
                            clearPickupPointForm();
                            setShowPickupForm(false);
                        }}>Back</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={addPickupPoint}>
                            {editingPickupPointId !== null ? "Update Pickup Point" : "Add Pickup Point"}
                        </Button>
                    </Flex>
                ) : activeTab === "google-data" ? null : (
                    <Flex justify="end" gap="small" className="facility-form-actions">
                        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
                        {activeTab === "treatment-notes" ? (
                            <Button type="primary" loading={saving} onClick={saveFacility}>Save</Button>
                        ) : (
                            <Button type="primary" onClick={goToNextTab}>Next</Button>
                        )}
                    </Flex>
                )}
            </Form>
        </>
    );
}
